import React, { useEffect, useState } from 'react'
import Explain from '../Explain'
import {
    actualGasCost,
    displayLocalPrice,
    getCurrentAccount,
    getEthOrLocalPrice,
    getGasPrice,
    getLocalPrice,
    isSafeToMint,
} from '../../eth/ethUtils'
import { ExpandLess, ExpandMore, HelpOutlineTwoTone } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import ShowEthOrCurrency from './ShowEthOrCurrency'
import { primaryColor } from 'assets/jss/material-kit-react'
import { getBrandName } from 'util/brandUtils'
import { getDisplayType } from 'redux/actions/displayActions'
import { isNarrow } from 'util/screenUtils'
import { useMediaQuery } from '@material-ui/core'
import TimerProgress from '../../components/mint/TimerProgress'

export default function TransactionCost({
    price,
    gasEstimate,
    mintFeeEth = 0,
    purchaseFee,
    transactionFeeEth = 0,
    action,
    ethInCurrency,
    ethBalance,
    count,
    actualGasCost,
    textAlign,
}) {
    const narrow = isNarrow(useMediaQuery)
    ///const textAlign = narrow ? 'left' : 'right'
    const [helpTarget, setHelpTarget] = useState()
    const [explain, setExplain] = useState()
    const [gasPrice, setGasPrice] = useState()
    const [showFeeBreakdown, setShowFeeBreakdown] = useState(false)
    //purchaseFee is a percentage inflated by 10
    const purchaseFeeEth = price && purchaseFee ? (price * purchaseFee) / 100 : 0

    const [ethAccount, setEthAccount] = useState(0)
    const { currency, showEthInCurrency } = useSelector((state) => state.settings)

    const explainGas = {
        title: `What is Ethereum Gas`,
        description: `<p>
      Every transaction on Ethereum is processed by a <i>miner</i>: someone or
      some company running a server that executes the logic for the
      transaction. Miners charge a fee to perform this service, and that fee
      is known as <i>gas</i>. The amount of gas charged depends on the
      complexity of the transaction. This is not a fee that ${getBrandName(
            getDisplayType()
        )} collects so we cannot refund it. 
    </p>
    <p>
      This price of gas -- measured in <i>gwei</i> -- varies depending on activity across the entire
      Ethereum network. Generally, the more activity, the more miners can
      charge for their service. Gas price can vary greatly during the course
      of a day or week. You can save your self money by minting your NFT when
      gas prices are low. Find the current average gas prices 
      at <a href="https://etherscan.io/gasTracker" target="_blank">
        this site
      </a>
      .
    </p>
    <p>The estimate you see here is based on the <b>current gas price of ${gasPrice} gwei</b>.</p>`,
    }
    const displaySafeMinting = () => {
        if (!isSafeToMint(gasEstimate, ethBalance, price)) {
            return (
                <p>
                    You do not have sufficient funds to safely {action} this NFT.{' '}
                    <HelpOutlineTwoTone
                        size='small'
                        color='primary'
                        onClick={(event) => {
                            setHelpTarget(event.currentTarget)
                            setExplain({
                                title: `Ensuring a successful ${action}`,
                                description: `<p>Gas estimates are just that -- an estimate.
                  Your ${action} make take more or less gas. If you do not have enough Ethereum to pay for the actual gas,
                  the transaction will fail and you will lose the gas you spent!</p>
                  <p>
                  To prevent that, we require that you have enough Ethereum funds to cover all cases`,
                            })
                        }}
                    />
                </p>
            )
        }
    }
    const displayPerCost = () => {
        if (count > 1) {
            const costPer = gasEstimate.gasInEth / count
            return (
                <>
                    {' '}
                    (
                    {getEthOrLocalPrice(
                        costPer,
                        ethInCurrency,
                        currency,
                        showEthInCurrency
                    )}{' '}
                    per NFT)
                </>
            )
        }
    }
    const displayActualCost = () => {
        const { priceInEth } = actualGasCost
        return (
            <div style={{ textAlign: textAlign }}>
                <p>
                    {`Your actual Ethereum gas cost to ${action} was ${getEthOrLocalPrice(
                        priceInEth,
                        ethInCurrency,
                        currency,
                        showEthInCurrency
                    )}`}
                    .{displayPerCost()}
                    <HelpOutlineTwoTone
                        style={{ cursor: 'pointer' }}
                        size='small'
                        color='primary'
                        onClick={(event) => {
                            setHelpTarget(event.currentTarget)
                            setExplain(explainGas)
                        }}
                    />
                </p>
                {price && (
                    <p>
                        The total cost to {action} was{' '}
                        {displayLocalPrice(
                            price + priceInEth,
                            ethInCurrency,
                            currency,
                            showEthInCurrency
                        )}
                    </p>
                )}
                <p>
                    Your new balance is{' '}
                    {displayLocalPrice(
                        ethBalance,
                        ethInCurrency,
                        currency,
                        showEthInCurrency
                    )}
                </p>

                <div style={{ flexDirection: 'row' }}>
                    <ShowEthOrCurrency />
                </div>
            </div>
        )
    }

    const feeBreakdown = () => {
        if (showFeeBreakdown) {
            return (
                <ExpandLess
                    style={{ verticalAlign: 'middle', cursor: 'pointer' }}
                    onClick={() => setShowFeeBreakdown(false)}
                />
            )
        } else {
            return (
                <ExpandMore
                    style={{
                        verticalAlign: 'middle',
                        cursor: 'pointer',
                    }}
                    onClick={() => setShowFeeBreakdown(true)}
                />
            )
        }
    }
    const displayFees = () => {
        const gasCostEth = parseFloat(gasEstimate.gasInEth)
        return (
            <div style={{ textAlign: textAlign }}>
                <h5>
                    {narrow && feeBreakdown()}
                    {`Service fees: ${getEthOrLocalPrice(
                        gasCostEth + mintFeeEth + purchaseFeeEth + transactionFeeEth,
                        ethInCurrency,
                        currency,
                        showEthInCurrency
                    )}`}
                    {!narrow && feeBreakdown()}
                </h5>
                {showFeeBreakdown ? (
                    <>
                        <p>
                            <HelpOutlineTwoTone
                                style={{
                                    cursor: 'pointer',
                                    height: '100%',
                                    verticalAlign: 'middle',
                                }}
                                size='small'
                                color='primary'
                                onClick={(event) => {
                                    setHelpTarget(event.currentTarget)
                                    setExplain(explainGas)
                                }}
                            />
                            {`Estimated Ethereum gas cost: ${getEthOrLocalPrice(
                                gasCostEth,
                                ethInCurrency,
                                currency,
                                showEthInCurrency
                            )}`}
                            <br />
                            {`${Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(gasCostEth)} ${process.env.REACT_APP_ETH_TEST==='true'?'Sepolia ':''} eth @ ${gasPrice} gwei`}                            
                            {displayPerCost()}
                        </p>
                        {mintFeeEth ? (
                            <p>
                                {getBrandName(getDisplayType())} {action} service fee:{' '}
                                {getEthOrLocalPrice(
                                    mintFeeEth,
                                    ethInCurrency,
                                    currency,
                                    showEthInCurrency
                                )}
                            </p>
                        ) : null}
                        {transactionFeeEth ? (
                            <p>
                                {getBrandName(getDisplayType())} {action} transaction fee:{' '}
                                {getEthOrLocalPrice(
                                    transactionFeeEth,
                                    ethInCurrency,
                                    currency,
                                    showEthInCurrency
                                )}
                            </p>
                        ) : null}
                        {purchaseFeeEth ? (
                            <p>
                                {getBrandName(getDisplayType())} {action}{' '}
                                {purchaseFee.toFixed(1)} percent purchase fee:
                                {displayLocalPrice(
                                    purchaseFeeEth,
                                    ethInCurrency,
                                    currency,
                                    showEthInCurrency
                                )}
                            </p>
                        ) : null}
                    </>
                ) : null}
            </div>
        )
    }
    const displayGasEstimate = () => {
        if (gasEstimate && gasPrice) {
            const gasCostEth = parseFloat(gasEstimate.gasInEth)
            const totalCost =
                (price ? parseFloat(price) : 0) + gasCostEth + purchaseFeeEth

            return (
                <div style={{ textAlign: textAlign }}>
                    {displayFees()}

                    {price ? (
                        <p>
                            {`Estimated cost: ${getEthOrLocalPrice(
                                totalCost,
                                ethInCurrency,
                                currency,
                                showEthInCurrency
                            )}`}
                        </p>
                    ) : null}

                    {/*<p>
            Your current balance is{' '}
            {displayLocalPrice(
              ethBalance,
              ethInCurrency,
              currency,
              showEthInCurrency
            )}
            </p>*/}
                    <h5>
                        {/*`Estimated Ethereum balance after ${action}: ${getEthOrLocalPrice(
              ethBalance - totalCost,
              ethInCurrency,
              currency,
              showEthInCurrency
            )}`*/}
                    </h5>
                    {displaySafeMinting()}
                    <div style={{ flexDirection: 'row' }}>
                        <ShowEthOrCurrency />
                    </div>
                </div>
            )
        } else {
            return <TimerProgress label='Getting gas estimate...' />
        }
    }

    const initEth = async () => {
        console.log('TransactionCost get current account')
        const currentAccount = await getCurrentAccount()
        console.log(`got current eth account ${currentAccount}`)
        setEthAccount(currentAccount)
        getGasPrice(setGasPrice)
    }
    useEffect(() => {
        initEth()
    }, [showEthInCurrency])

    if (ethAccount) {
        return (
            <div
                style={{
                    border: '0.5px solid ' + primaryColor,
                    margin: '0.5em',
                    padding: '1em',
                    borderRadius: '0.5em',
                    width: '90%',
                }}
            >
                {actualGasCost ? displayActualCost() : displayGasEstimate()}
                {explain && (
                    <Explain
                        source={explain}
                        target={helpTarget}
                        close={() => setExplain(null)}
                    />
                )}
            </div>
        )
    } else {
        return <TimerProgress />
    }
}